import { useEffect, useRef, useState } from "react";
import {
  CompaingsDataType,
  languagesDataType,
} from "../../interfaces/admin.interface";
import { Helmet } from "react-helmet";
import { message, Modal, Popconfirm, Table, TableProps } from "antd";
import * as Yup from "yup";
import { useFormik } from "formik";
import { LanguageService } from "../../services/languages.service";
import ErrorMessage from "../../components/shared/ErrorMessage/ErrorMessage";
import MainButton from "../../components/shared/MainButton/MainButton";
import Spinner from "../../components/shared/Spinner/Spinner";
import { CampaignsService } from "../../services/campaings.service";
import ShowImage from "../../components/shared/show-image/ShowImage";
import SelectImage from "../../components/shared/select-image/SelectImage";
import { FaPlus } from "react-icons/fa";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import $axios from "../../api/accessor";
import ChangeLanguage from "../../components/shared/change-language/ChangeLanguage";

type TranslationType = {
  lang_code: string;
  name: string;
  text: string;
  description: string;
};

const Campaigns = () => {
  const [campaigns, setCampaigns] = useState<CompaingsDataType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingRecord, setEditingRecord] = useState<CompaingsDataType | null>(
    null,
  );

  const [langs, setLangs] = useState<languagesDataType[]>([]);
  const [primaryLangCode, setPrimaryLangCode] = useState<string>("UZ");
  const [langCode, setLangCode] = useState<string>("UZ");

  const [logoFileList, setLogoFileList] = useState<any[]>([]);
  const [innerImageFileList, setInnerImageFileList] = useState<any[]>([]);

  const [showImageModal, setShowImageModal] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string>("");

  const initialCampValues = useRef({
    name: "",
    description: "",
    text: "",
    start_date: "",
    end_date: "",
    cover_image: "",
    image: "",
    translations: [] as TranslationType[],
  });

  useEffect(() => {
    setLoading(true);
    CampaignsService.getAllCampaigns()
      .then((response) => {
        const list: CompaingsDataType[] = response?.data?.data ?? [];
        setCampaigns(
          list.map((item: CompaingsDataType, index: number) => ({
            ...item,
            key: index + 1,
          })),
        );
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    LanguageService.getAllLanguages().then((response) => {
      const data: languagesDataType[] = response?.data ?? [];
      setLangs(data);

      const hasUZ =
        data.find((l) => String(l.code).toUpperCase() === "UZ")?.code ?? null;

      const nextPrimary = hasUZ ?? data?.[0]?.code ?? "UZ";
      setPrimaryLangCode(nextPrimary);
      setLangCode(nextPrimary);
    });
  }, []);

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    return dateString.split(" ")[0];
  };

  const fetchCampaigns = async () => {
    const updatedData = await CampaignsService.getAllCampaigns();
    const list: CompaingsDataType[] = updatedData?.data?.data ?? [];
    setCampaigns(
      list.map((item: CompaingsDataType, index: number) => ({
        ...item,
        key: index + 1,
      })),
    );
  };

  const handleAfterSubmit = async () => {
    setLoading(true);
    setIsModalVisible(false);
    resetForm();
    await fetchCampaigns();
    setLoading(false);
  };

  const {
    handleSubmit,
    handleChange,
    values,
    resetForm,
    setFieldValue,
    touched,
    errors,
  } = useFormik({
    initialValues: {
      name: "",
      description: "",
      text: "",
      translations: [] as TranslationType[],
      start_date: "",
      end_date: "",
      cover_image: "",
      image: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Slayder adı tələb olunur"),
      start_date: Yup.string().required("Slayder başlama tarixi tələb olunur"),
      end_date: Yup.string().required("Slayder bitmə tarixi tələb olunur"),
      cover_image: Yup.string().required("Slayder şəkili tələb olunur"),
      image: Yup.string().required("Daxili şəkil tələb olunur"),
    }),
    onSubmit: async (vals) => {
      // Primary dili həmişə root field-lərdən formalaşdırırıq
      const primaryTranslation: TranslationType = {
        lang_code: primaryLangCode,
        name: vals.name,
        text: vals.text,
        description: vals.description,
      };

      const otherTranslations = (vals.translations || []).filter(
        (t) => t.lang_code !== primaryLangCode,
      );

      const finalValues = {
        name: vals.name,
        description: vals.description,
        text: vals.text,
        start_date: vals.start_date,
        end_date: vals.end_date,
        cover_image: vals.cover_image,
        image: vals.image,
        translations: [primaryTranslation, ...otherTranslations],
      };

      try {
        if (isEditing && editingRecord) {
          const isEqual =
            finalValues.name === initialCampValues.current.name &&
            finalValues.description === initialCampValues.current.description &&
            finalValues.text === initialCampValues.current.text &&
            finalValues.start_date ===
              formatDate(initialCampValues.current.start_date) &&
            finalValues.end_date ===
              formatDate(initialCampValues.current.end_date) &&
            finalValues.cover_image === initialCampValues.current.cover_image &&
            finalValues.image === initialCampValues.current.image &&
            JSON.stringify(finalValues.translations) ===
              JSON.stringify(initialCampValues.current.translations);

          if (isEqual) {
            message.info("Dəyişiklik edilmədi.");
            setLoading(false);
            return;
          }

          const payload: any = {
            name: finalValues.name,
            description: finalValues.description,
            text: finalValues.text,
            start_date: finalValues.start_date,
            end_date: finalValues.end_date,
            translations: finalValues.translations,
          };

          // Şəkil dəyişməyibsə göndərmə (istəsən silə bilərsən)
          if (editingRecord.cover_image !== finalValues.cover_image) {
            payload.cover_image = finalValues.cover_image;
          }
          if (editingRecord.image !== finalValues.image) {
            payload.image = finalValues.image;
          }

          const response = await CampaignsService.updateCampaign({
            ...payload,
            id: editingRecord.id,
          });

          if (response.status === 200) {
            message.success("Slayder uğurla yeniləndi!");
            await handleAfterSubmit();
          } else {
            message.error(
              response.errorMessage || "Slayderi yeniləmək alınmadı.",
            );
          }
        } else {
          const response = await CampaignsService.addCampaign(finalValues);
          if (response.status === 200) {
            message.success("Slayder uğurla əlavə edildi!");
            await handleAfterSubmit();
          } else {
            message.error(
              response.errorMessage || "Slayder əlavə etmək alınmadı.",
            );
          }
        }
      } finally {
        setLoading(false);
      }
    },
  });

  // Primary dil root field-lər, digər dillər translations-dan oxunur
  const getCurrentTranslation = () => {
    if (langCode === primaryLangCode) {
      return {
        name: values.name,
        text: values.text,
        description: values.description,
      };
    }
    return (
      values.translations.find((t) => t.lang_code === langCode) || {
        name: "",
        text: "",
        description: "",
      }
    );
  };

  const handleTranslationChange = (
    value: string,
    field: "name" | "text" | "description",
  ) => {
    if (langCode === primaryLangCode) {
      setFieldValue(field, value);
      return;
    }

    const updatedTranslations = [...(values.translations || [])];
    const idx = updatedTranslations.findIndex((t) => t.lang_code === langCode);

    if (idx !== -1) {
      updatedTranslations[idx] = {
        ...updatedTranslations[idx],
        [field]: value,
      };
    } else {
      const newT: TranslationType = {
        lang_code: langCode,
        name: "",
        text: "",
        description: "",
      };
      newT[field] = value as any;
      updatedTranslations.push(newT);
    }
    setFieldValue("translations", updatedTranslations);
  };

  const handleDeleteClick = async (record: CompaingsDataType) => {
    try {
      await CampaignsService.deleteCompaing(record.id.toString());
      setCampaigns((prev) => prev.filter((i) => i.id !== record.id));
      message.success("Slayderi uğurla silindi!");
    } catch {
      message.error("Slayderi silmək alınmadı.");
    }
  };

  const handleAddClick = () => {
    setIsEditing(false);
    setEditingRecord(null);
    setIsModalVisible(true);

    resetForm();
    setLangCode(primaryLangCode);

    setFieldValue("cover_image", "");
    setFieldValue("image", "");
    setFieldValue("translations", []);

    setLogoFileList([]);
    setInnerImageFileList([]);
  };

  const handleEditClick = (record: CompaingsDataType) => {
    setIsEditing(true);
    setEditingRecord(record);
    setIsModalVisible(true);

    setLangCode(primaryLangCode);

    setFieldValue("name", record.name || "");
    setFieldValue("description", record.description || "");
    setFieldValue("text", record.text || "");
    setFieldValue("start_date", formatDate(record.start_date || ""));
    setFieldValue("end_date", formatDate(record.end_date || ""));
    setFieldValue("cover_image", record.cover_image || "");
    setFieldValue("image", record.image || "");

    const recTranslations: TranslationType[] =
      (record.translations as any)?.map((t: any) => ({
        lang_code: t.lang_code,
        name: t.name || "",
        text: t.text || "",
        description: t.description || "",
      })) || [];

    // primary translation varsa values.translations-a qoymuruq (root artıq primary-dir)
    setFieldValue(
      "translations",
      recTranslations.filter((t) => t.lang_code !== primaryLangCode),
    );

    initialCampValues.current = {
      name: record.name || "",
      description: record.description || "",
      text: record.text || "",
      start_date: record.start_date || "",
      end_date: record.end_date || "",
      cover_image: record.cover_image || "",
      image: record.image || "",
      translations: [
        {
          lang_code: primaryLangCode,
          name: record.name || "",
          text: record.text || "",
          description: record.description || "",
        },
        ...recTranslations.filter((t) => t.lang_code !== primaryLangCode),
      ],
    };

    setLogoFileList(
      record.cover_image
        ? [
            {
              uid: "-1",
              name: `${record.name || "cover"}`,
              status: "done",
              url: record.cover_image,
            },
          ]
        : [],
    );

    setInnerImageFileList(
      record.image
        ? [
            {
              uid: "-1",
              name: `${record.name || "inner"}`,
              status: "done",
              url: record.image,
            },
          ]
        : [],
    );
  };

  const columns: TableProps<CompaingsDataType>["columns"] = [
    {
      title: "№",
      dataIndex: "key",
      sorter: (a, b) => (a.key ?? 0) - (b.key ?? 0),
    },
    {
      title: "Adı",
      dataIndex: "name",
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: "Təsvir",
      dataIndex: "description",
      render: (_, record) => (
        <div>
          {(record.description || "").length > 20
            ? (record.description || "").substring(0, 20) + "..."
            : record.description || ""}
        </div>
      ),
    },
    {
      title: "Mətn",
      dataIndex: "text",
      render: (text) => (
        <div>
          {text && text.length > 30 ? `${text.substring(0, 30)}...` : text}
        </div>
      ),
    },
    {
      title: "Başlanğıc tarixi",
      dataIndex: "start_date",
      render: (date) => <div>{formatDate(date)}</div>,
    },
    {
      title: "Bitmə tarixi",
      dataIndex: "end_date",
      render: (date) => <div>{formatDate(date)}</div>,
    },
    {
      title: "Şəkil",
      dataIndex: "cover_image",
      render: (_, record) => (
        <div
          style={{ cursor: "pointer" }}
          onClick={() => {
            setSelectedImage(record.cover_image);
            setShowImageModal(true);
          }}
        >
          {record.cover_image ? (
            <img
              style={{ width: "40px" }}
              src={record.cover_image}
              alt={`${record.name || ""}`}
            />
          ) : null}
        </div>
      ),
    },
    {
      title: "Daxili şəkil",
      dataIndex: "image",
      render: (_, record) => (
        <div
          style={{ cursor: "pointer" }}
          onClick={() => {
            setSelectedImage(record.image);
            setShowImageModal(true);
          }}
        >
          {record.image ? (
            <img
              style={{ width: "40px" }}
              src={record.image}
              alt={`${record.name || ""}`}
            />
          ) : null}
        </div>
      ),
    },
    {
      title: (
        <div className="settings">
          <i className="fa-solid fa-gear"></i>
        </div>
      ),
      key: "action",
      render: (_, record) => (
        <div className="settings_icons">
          <div className="edit" onClick={() => handleEditClick(record)}>
            <i className="fa-solid fa-pen-to-square"></i>
          </div>
          <Popconfirm
            title="Bu Slayderi siləcəyinizə əminsiniz?"
            onConfirm={() => handleDeleteClick(record)}
            onCancel={() => message.info("Silmə əməliyyatı ləğv edildi.")}
            okText="Bəli"
            cancelText="Xeyr"
            placement="topRight"
          >
            <div className="delete">
              <i className="fa-solid fa-trash"></i>
            </div>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const currentTranslation = getCurrentTranslation();

  return (
    <div id="pages">
      <Helmet>
        <title>Orhan Door | Slayderlər</title>
      </Helmet>

      {loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="page_head">
            <h3 className="page_title">Slayderlər</h3>
          </div>

          <div className="pages_components">
            <MainButton onClick={handleAddClick}>
              <FaPlus size={20} />
            </MainButton>

            <div className="pages_table">
              <DragDropContext
                onDragEnd={(result) => {
                  if (!result.destination) return;

                  const items = Array.from(campaigns);
                  const [reorderedItem] = items.splice(result.source.index, 1);
                  items.splice(result.destination.index, 0, reorderedItem);

                  setCampaigns(items);

                  try {
                    const order = items.map((i, idx) => ({
                      id: i.id,
                      sort_index: idx + 1,
                    }));

                    (async () => {
                      try {
                        await $axios.post("/admin/campaigns/reorder", {
                          order,
                        });
                      } catch (err: any) {
                        message.error(
                          "Sıralama yadda saxlanmadı. Yenidən cəhd edin.",
                        );
                      }
                    })();
                  } catch {}
                }}
              >
                <Table
                  scroll={{ x: "100%" }}
                  columns={columns}
                  dataSource={campaigns.map((item, index) => ({
                    ...item,
                    key: index + 1,
                  }))}
                  rowKey={(record) => record.id.toString()}
                  pagination={false}
                  components={{
                    body: {
                      wrapper: (bodyProps: any) => (
                        <Droppable droppableId="campaigns" direction="vertical">
                          {(dropProvided) => (
                            <tbody
                              {...bodyProps}
                              ref={dropProvided.innerRef}
                              {...dropProvided.droppableProps}
                            >
                              {bodyProps.children}
                              {dropProvided.placeholder}
                            </tbody>
                          )}
                        </Droppable>
                      ),
                      row: (props: any) => {
                        const rowKey = props["data-row-key"];
                        const rowIndex = campaigns.findIndex(
                          (i) => i.id.toString() === rowKey.toString(),
                        );

                        return (
                          <Draggable
                            draggableId={rowKey.toString()}
                            index={rowIndex}
                          >
                            {(dragProvided, snapshot) => (
                              <tr
                                {...props}
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                {...dragProvided.dragHandleProps}
                                style={{
                                  ...props.style,
                                  ...dragProvided.draggableProps.style,
                                  cursor: snapshot.isDragging
                                    ? "grabbing"
                                    : "grab",
                                  background: snapshot.isDragging
                                    ? "#ffffff"
                                    : undefined,
                                  boxShadow: snapshot.isDragging
                                    ? "0 8px 20px rgba(0,0,0,0.18)"
                                    : undefined,
                                  transition:
                                    "transform 200ms ease, box-shadow 200ms ease, background-color 200ms ease",
                                }}
                              >
                                {props.children}
                              </tr>
                            )}
                          </Draggable>
                        );
                      },
                    },
                  }}
                />
              </DragDropContext>
            </div>
          </div>
        </div>
      )}

      <Modal
        title={isEditing ? "Slayderi redaktə edin" : "Yeni slayder əlavə edin"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        style={{ minWidth: "340px" }}
        centered
      >
        <ChangeLanguage
          langCode={langCode}
          setLangCode={setLangCode}
          values={values}
          setFieldValue={setFieldValue}
          langs={langs}
        />

        <form className="modal_form" onSubmit={handleSubmit}>
          <div className="form_input">
            <label className="label" htmlFor="name">
              Slayder adı ({langCode})
            </label>
            <div className="input_wrapper">
              <input
                name="name"
                id="name"
                placeholder="Slayder adı"
                value={currentTranslation.name}
                onChange={(e) =>
                  handleTranslationChange(e.target.value, "name")
                }
              />
              <span className="focus_border"></span>
            </div>

            {langCode === primaryLangCode && (
              <ErrorMessage error={errors.name} touched={touched.name} />
            )}
          </div>

          <div className="form_input">
            <label className="label" htmlFor="description">
              Slayder təsviri ({langCode})
            </label>
            <div className="input_wrapper">
              <input
                name="description"
                id="description"
                placeholder="Slayder təsviri"
                value={currentTranslation.description}
                onChange={(e) =>
                  handleTranslationChange(e.target.value, "description")
                }
              />
              <span className="focus_border"></span>
            </div>
          </div>

          <div className="form_input">
            <label className="label" htmlFor="text">
              Slayder mətni ({langCode})
            </label>
            <div className="input_wrapper">
              <input
                type="text"
                name="text"
                id="text"
                placeholder="Slayder mətni"
                value={currentTranslation.text}
                onChange={(e) =>
                  handleTranslationChange(e.target.value, "text")
                }
              />
              <span className="focus_border"></span>
            </div>
          </div>

          {/* Tarixlər artıq heç bir dilə görə şərtlənmir */}
          <div className="form_input">
            <label className="label" htmlFor="start_date">
              Başlanğıc tarixi
            </label>
            <div className="input_wrapper">
              <input
                type="date"
                name="start_date"
                id="start_date"
                value={values.start_date || ""}
                onChange={handleChange}
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage
              error={errors.start_date}
              touched={touched.start_date}
            />
          </div>

          <div className="form_input">
            <label className="label" htmlFor="end_date">
              Bitmə tarixi
            </label>
            <div className="input_wrapper">
              <input
                type="date"
                name="end_date"
                id="end_date"
                value={values.end_date || ""}
                onChange={handleChange}
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={errors.end_date} touched={touched.end_date} />
          </div>

          {/* Şəkillər: primary dil üçün root, digər dillər üçün translations */}
          <div className="select_images">
            <SelectImage
              image_name="Əsas şəkili seçin"
              logoFileList={logoFileList}
              setLogoFileList={setLogoFileList}
              field="cover_image"
              setFieldValue={setFieldValue}
            />

            <SelectImage
              image_name="Daxili şəkili seçin"
              logoFileList={innerImageFileList}
              setLogoFileList={setInnerImageFileList}
              field="image"
              setFieldValue={setFieldValue}
            />
          </div>

          <div>
            <MainButton full type="submit" className="form_add_btn">
              {isEditing ? "Redaktə edin" : "Əlavə edin"}
            </MainButton>
          </div>
        </form>
      </Modal>

      <ShowImage
        showImageModal={showImageModal}
        setShowImagesModal={setShowImageModal}
        image={selectedImage}
      />
    </div>
  );
};

export default Campaigns;
